import type { JSX } from "react";
import type { TFunction } from "i18next";
import {
  FOCAL_CENTER,
  FOCAL_SURFACES,
  FOCAL_SURFACE_IDS,
  HERO_SCRIM_ALPHA,
  focalObjectPosition,
  focalObjectPositionFromPoint,
  type FocalLike,
  type FocalSurfaceId,
} from "@ckgo2/shared";

/**
 * Náhledy ploch webu v detailu fotky (vize podnety-tymu-2026-10, F8.4): pět rámečků s poměrem
 * plochy z `FOCAL_SURFACES` v pořadí `FOCAL_SURFACE_IDS`. Výřez kreslí prohlížeč stejně jako
 * web (`object-fit: cover` + `object-position`), takže je z definice týž; pozice jde jen
 * z kanonického modulu ohniska — `focalObjectPosition` podle stavu (bod u `ai` a `manual`),
 * u `auto` střed, který prohlížeč na webu kreslí bez atributu. Náhledy jdou za bodem formuláře,
 * takže se mění už při tažení, před uložením.
 *
 * Oba hero náhledy mají ukázkový nadpis nad ztmavením s krytím `HERO_SCRIM_ALPHA.nadpis`
 * (jeden zdroj čísel s webem); nadpis je potomek obalu ztmavení, takže plné krytí leží pod
 * celým nadpisem i po zalomení. Tvar vrstvy webu (`HeroScrim.astro`) se nekopíruje, shodné je
 * krytí pod nadpisem. Čistá komponenta bez hooků, `t` v props (precedent `PickerTile`).
 */

export interface FocalSurfacePreviewsProps {
  src: string;
  focal: FocalLike;
  t: TFunction;
}

const HERO_SURFACES: ReadonlySet<FocalSurfaceId> = new Set(["heroMobile", "heroDesktop"]);

function surfaceLabels(t: TFunction): Record<FocalSurfaceId, string> {
  return {
    heroMobile: t("media.focalPreviews.heroMobile"),
    heroDesktop: t("media.focalPreviews.heroDesktop"),
    card16x10: t("media.focalPreviews.card16x10"),
    card3x4: t("media.focalPreviews.card3x4"),
    square: t("media.focalPreviews.square"),
  };
}

/*
 * Nadpis a okraje jsou v měřítku náhledu: hero mobil je při výšce 112 px široký ~52 px, takže
 * `text-sm` s `px-2` by nadpis zalomil do tří řádků, přetekl rámeček a ztmavení by krylo ~75 %
 * náhledu, mnohem víc než nadpis na webovém hero. Přechod do průhledna má výšku horního okraje
 * (`pt-3` = 0.75rem), takže plné krytí začíná právě nad nadpisem.
 */
const SCRIM_STYLE = {
  background: `linear-gradient(to top, rgba(0, 0, 0, ${HERO_SCRIM_ALPHA.nadpis}) calc(100% - 0.75rem), transparent)`,
};

function heroScrim(t: TFunction): JSX.Element {
  return (
    <div
      data-focal-preview-scrim=""
      data-alpha={String(HERO_SCRIM_ALPHA.nadpis)}
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 px-1 pb-1 pt-3"
      style={SCRIM_STYLE}
    >
      <span
        data-focal-preview-heading=""
        className="block text-[9px] font-bold leading-tight text-white"
      >
        {t("media.focalPreviews.sampleHeading")}
      </span>
    </div>
  );
}

export default function FocalSurfacePreviews({
  src,
  focal,
  t,
}: FocalSurfacePreviewsProps): JSX.Element {
  const position =
    focalObjectPosition(focal) ?? focalObjectPositionFromPoint(FOCAL_CENTER, FOCAL_CENTER);
  const labels = surfaceLabels(t);
  return (
    <section data-focal-previews="">
      <h3 className="label">{t("media.focalPreviews.title")}</h3>
      <div className="flex flex-wrap items-end gap-2">
        {FOCAL_SURFACE_IDS.map((id) => {
          const { w, h } = FOCAL_SURFACES[id];
          return (
            <figure key={id} data-focal-preview={id} data-object-position={position}>
              <div
                className="relative h-28 shrink-0 overflow-hidden rounded border border-gray-200 dark:border-gray-700"
                style={{ aspectRatio: `${w} / ${h}` }}
              >
                {/* Alt fotky nese editor bodu; náhledy jsou dekorativní opakování téže fotky. */}
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-cover"
                  style={{ objectPosition: position }}
                  draggable={false}
                />
                {HERO_SURFACES.has(id) && heroScrim(t)}
              </div>
              <figcaption className="mt-0.5 text-center text-[11px] text-gray-500">
                {labels[id]}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
}
